import React, { useEffect, useState } from 'react';
import { 
  Page, 
  Card, 
  Layout, 
  Text, 
  BlockStack, 
  Spinner, 
  InlineGrid,
  ProgressBar,
  InlineStack,
  Button,
  Banner,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { statsApi } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { isLocalDevelopment } from '../utils/env';
import CrownIcon from '../components/CrownIcon';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { shopId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    pendingReviews: 0,
    productsWithReviews: 0,
    ratingDistribution: {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    },
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!shopId) {
        // Modo desenvolvimento local - não fazer requisições
        setLoading(false);
        return;
      }

      setLoading(true);
      
      // Buscar stats
      const result = await statsApi.getStats(shopId);
      
      if (result.data) {
        setStats({
          totalReviews: result.data.total_reviews || 0,
          averageRating: result.data.average_rating || 0,
          pendingReviews: result.data.pending_reviews || 0,
          productsWithReviews: result.data.products_with_reviews || 0,
          ratingDistribution: result.data.rating_distribution || {
            5: 0, 4: 0, 3: 0, 2: 0, 1: 0,
          },
        });
      }

      // Buscar status da assinatura
      if (!isLocalDevelopment()) {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
          
          const response = await fetch(
            `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
            {
              headers: {
                'apikey': supabaseAnonKey,
                'Authorization': `Bearer ${supabaseAnonKey}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            setIsPaid(data.plan === 'paid' && data.status === 'active');
          }
        } catch (err) {
          // Em caso de erro, assumir plano free
          setIsPaid(false);
        }
      }
      
      setLoading(false);
    };

    fetchStats();
  }, [shopId]);

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <BlockStack gap="300" align="center">
                  <Spinner size="large" />
                  <Text as="p" tone="subdued" alignment="center">
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </Text>
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Calcular total de ratings para porcentagem
  const totalRatings = Object.values(stats.ratingDistribution).reduce((sum, count) => sum + count, 0);
  
  // Calcular porcentagem de cada rating
  const getRatingPercentage = (count: number) => {
    if (totalRatings === 0) return 0;
    return Math.round((count / totalRatings) * 100);
  };

  return (
    <Page
      title={t('dashboard.title', { defaultValue: 'Dashboard' })}
      primaryAction={{
        content: t('dashboard.viewAllReviews', { defaultValue: 'Ver Todas as Avaliações' }),
        onAction: () => navigate('/reviews'),
      }}
      secondaryActions={[
        {
          content: t('dashboard.viewProducts', { defaultValue: 'Ver Produtos' }),
          onAction: () => navigate('/products'),
        },
      ]}
    >
      <Layout>
        {/* Banner de Upgrade quando está no plano FREE */}
        {!isPaid && !loading && (
          <Layout.Section>
            <Banner
              tone="info"
              title={t('dashboard.upgradeBanner.title', { defaultValue: 'Upgrade para Plano Pro' })}
              action={{
                content: t('dashboard.upgradeBanner.action', { defaultValue: 'Ver Planos' }),
                onAction: () => navigate('/billing'),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <CrownIcon size={20} />
                <p style={{ margin: 0 }}>
                  {t('dashboard.upgradeBanner.message', { 
                    defaultValue: 'Você está no plano gratuito. Desbloqueie produtos ilimitados e recursos avançados!' 
                  })}
                </p>
              </div>
              <p>
                {t('dashboard.upgradeBanner.message', { 
                  defaultValue: 'Você está no plano gratuito. Desbloqueie produtos ilimitados e recursos avançados!' 
                })}
              </p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <BlockStack gap="500">
            {/* Cards de Estatísticas */}
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3" tone="subdued">
                    {t('dashboard.stats.totalReviews', { defaultValue: 'Total de Avaliações' })}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    {stats.totalReviews}
                  </Text>
                  {stats.totalReviews > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {stats.totalReviews - stats.pendingReviews} {t('dashboard.stats.approved', { defaultValue: 'aprovadas' })}
                    </Text>
                  )}
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3" tone="subdued">
                    {t('dashboard.stats.averageRating', { defaultValue: 'Avaliação Média' })}
                  </Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="heading2xl" as="p">
                      {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '0.0'}
                    </Text>
                    {stats.averageRating > 0 && (
                      <Text as="span" variant="bodyLg" tone="subdued">
                        {'⭐'.repeat(Math.round(stats.averageRating))}
                      </Text>
                    )}
                  </InlineStack>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3" tone="subdued">
                    {t('dashboard.stats.pendingReviews', { defaultValue: 'Avaliações Pendentes' })}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    {stats.pendingReviews}
                  </Text>
                  {stats.pendingReviews > 0 && (
                    <Button
                      size="micro"
                      onClick={() => navigate('/reviews?filter=pending')}
                    >
                      {t('dashboard.reviewNow', { defaultValue: 'Revisar Agora' })}
                    </Button>
                  )}
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3" tone="subdued">
                    {t('dashboard.stats.productsWithReviews', { defaultValue: 'Produtos com Avaliações' })}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    {stats.productsWithReviews}
                  </Text>
                </BlockStack>
              </Card>
            </InlineGrid>

            {/* Distribuição de Ratings */}
            {totalRatings > 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    {t('dashboard.ratingDistribution', { defaultValue: 'Distribuição de Avaliações' })}
                  </Text>
                  
                  <BlockStack gap="300">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution];
                      const percentage = getRatingPercentage(count);
                      
                      return (
                        <BlockStack key={rating} gap="100">
                          <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="200" blockAlign="center">
                              <Text as="span" variant="bodyMd" fontWeight="semibold">
                                {rating} ⭐
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {count} {t('dashboard.reviews', { defaultValue: 'avaliações' })}
                              </Text>
                            </InlineStack>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {percentage}%
                            </Text>
                          </InlineStack>
                          <ProgressBar progress={percentage} size="small" />
                        </BlockStack>
                      );
                    })}
                  </BlockStack>
                </BlockStack>
              </Card>
            )}

            {/* Ações Rápidas */}
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  {t('dashboard.quickActions', { defaultValue: 'Ações Rápidas' })}
                </Text>
                
                <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/reviews')}
                  >
                    {t('dashboard.viewReviews', { defaultValue: 'Ver Avaliações' })}
                  </Button>
                  <Button
                    onClick={() => navigate('/products')}
                  >
                    {t('dashboard.viewProducts', { defaultValue: 'Ver Produtos' })}
                  </Button>
                  <Button
                    onClick={() => navigate('/widget')}
                  >
                    {t('dashboard.configureWidget', { defaultValue: 'Configurar Widget' })}
                  </Button>
                </InlineGrid>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Dashboard;
